/**
 * App-wide toast primitive.
 *
 * A single, token-driven feedback surface shared by every bidder + admin
 * mutation flow. It renders in a fixed viewport (top-right) relative to the
 * window — NOT the top of the document — so a "Saved." confirmation is always
 * visible regardless of how far down a long form the Save button sits. This
 * replaces ~7 hand-rolled inline `AlertBox` copies and the several ad-hoc
 * success-banner patterns scattered across pages.
 *
 * Policy: success/info → transient toast; form-validation errors stay pinned
 * inline near the field. `toast.error(...)` exists for mutation failures that
 * are better surfaced globally (it dwells longer than success).
 *
 * Colours come from tokens with `var(--x, fallback)` so the toast inherits the
 * bidder theme, the admin theme, dark mode, and any tenant brand automatically.
 * Lives in apps/web for now (its only consumer); promote to @auctisync/ui if
 * apps/help or apps/marketing ever need it.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

type ToastTone = 'success' | 'error' | 'info';

type ToastItem = { id: number; tone: ToastTone; message: string };

export type ToastApi = {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

const SUCCESS_DURATION_MS = 3200;
const ERROR_DURATION_MS = 6000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string) => {
      const id = (idRef.current += 1);
      setToasts((list) => [...list, { id, tone, message }]);
      window.setTimeout(
        () => dismiss(id),
        tone === 'error' ? ERROR_DURATION_MS : SUCCESS_DURATION_MS,
      );
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message: string) => push('success', message),
      error: (message: string) => push('error', message),
      info: (message: string) => push('info', message),
    }),
    [push],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

const TONE_PALETTE: Record<ToastTone, { bg: string; fg: string; border: string; accent: string }> =
  {
    success: {
      bg: 'var(--surface-success, #f0fdf4)',
      fg: 'var(--text-success, #166534)',
      border: 'var(--border-success, #bbf7d0)',
      accent: 'var(--semantic-no-reserve, #059669)',
    },
    error: {
      bg: 'var(--surface-danger, #fef2f2)',
      fg: 'var(--text-danger, #991b1b)',
      border: 'var(--border-danger, #fecaca)',
      accent: 'var(--semantic-outbid, #dc2626)',
    },
    info: {
      bg: 'var(--surface-elev, #ffffff)',
      fg: 'var(--text, #0f172a)',
      border: 'var(--brand-hairline, var(--border, #e2e8f0))',
      accent: 'var(--brand-accent, #2264dc)',
    },
  };

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div aria-live="polite" style={viewportStyle}>
      <style>{KEYFRAMES}</style>
      {toasts.map((t) => {
        const palette = TONE_PALETTE[t.tone];
        return (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            style={{
              ...toastStyle,
              background: palette.bg,
              color: palette.fg,
              border: `1px solid ${palette.border}`,
              borderLeft: `4px solid ${palette.accent}`,
            }}
          >
            <span style={{ flex: 1 }}>{t.message}</span>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => onDismiss(t.id)}
              style={dismissStyle}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}

const KEYFRAMES = `@keyframes as-toast-in {
  from { opacity: 0; transform: translateY(-0.5rem); }
  to { opacity: 1; transform: translateY(0); }
}`;

const viewportStyle: CSSProperties = {
  position: 'fixed',
  top: '1rem',
  right: '1rem',
  left: 'auto',
  zIndex: 9999,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  maxWidth: 'min(24rem, calc(100vw - 2rem))',
  pointerEvents: 'none',
};

const toastStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.6rem',
  padding: '0.7rem 0.85rem',
  borderRadius: 'var(--r-control, 0.5rem)',
  boxShadow: 'var(--elev-3, 0 8px 24px rgba(15,23,42,0.18))',
  fontSize: '0.875rem',
  lineHeight: 1.35,
  pointerEvents: 'auto',
  animation: 'as-toast-in 0.16s ease-out',
};

const dismissStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'inherit',
  fontSize: '1.1rem',
  lineHeight: 1,
  cursor: 'pointer',
  opacity: 0.65,
  padding: 0,
  marginTop: '-0.1rem',
};
